import {EstablishedAhpComposerProvider} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useEstablishedAhpComposerFocusReturn} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-composer-focus-return'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import type {
  AhpSpaceSelection,
  DispatchReceipt,
  DirectTurnTelemetryContext,
  DirectTurnSurface,
  EstablishedAhpSession,
} from '@github-ui/ahp-session-client/contracts'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {HOST_INDEPENDENT_CLIENT_TOOLS} from '@github-ui/copilot-shared-ui/utils/ahp-client-tools'
import {AhpSessionComposer} from '@github-ui/one-copilot-composer/existing'
import {announce} from '@github-ui/aria-live'
import {useEntitlementQuotaNotice} from '@github-ui/copilot-shared-ui/hooks/use-entitlement-quota-notice'
import {INCREASE_INDIVIDUAL_BUDGET_URL} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Link} from '@primer/react'

import {usePermissionCommandHandler} from '../../hooks/use-permission-command-handler'
import {taskHasRepository, useRepositoryCommandGuard} from '../../hooks/use-repository-command-guard'
import {useShareCommandHandler} from '../../hooks/use-share-command-handler'
import {useStaffDebugFeedbackToast} from '../../hooks/use-staff-debug-feedback-toast'
import {useLatestAgentModel} from '@github-ui/one-copilot-composer/use-latest-agent-model'
import {useLatestReasoningEffort} from '@github-ui/one-copilot-composer/use-latest-reasoning-effort'
import {OverlayDirectionProvider} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {
  reasoningEffortForModel,
  useModelSupportedReasoningEfforts,
  useReasoningEffortAutoFallback,
  useSelectModelClearingReasoningEffort,
} from '@github-ui/one-copilot-composer/use-model-supported-reasoning-efforts'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type RefObject} from 'react'
import type {ClientTool, RelayConnectionProgress} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {RelayFailure} from '@github-ui/ahp-relay/relay-failure'

import {EstablishedModeToolbarSlot} from './EstablishedModeToolbarSlot'
import {AhpTaskChatInputActions} from './AhpTaskChatInputActions'
import {TaskChatInputHeader} from './TaskChatInputHeader'
import {QuotaBanner} from './QuotaBanner'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {useUnarchiveTask} from '../../hooks/use-unarchive-task'
import {useViewMode} from '../../contexts/ViewModeContext'
import {useFreshQuotaSnapshot} from '../../hooks/use-fresh-quota-snapshot'
import {baseRefMatchesHeadRef, getBranchArtifactData} from '../../utils/task-artifact-helpers'
import {usePromptResponseSubmission} from '../../hooks/use-prompt-response-submission'
import type {TaskChatInputProps} from './TaskChatInputV2'
import {SessionContextProvider} from '../../contexts/SessionContext'
import {BlockedTaskInput} from './BlockedTaskInput'
import {useOptionalContributedInstructionSnapshot as useLegacyContributedInstructionSnapshot} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {
  useHasContributedInstructionsProvider,
  useOptionalContributedInstructionSnapshot,
} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'
import {toContributedInstructionsMetadata} from '@github-ui/copilot-chat/utils/instruction-rules'
import {useAhpSessionEvents} from '../../hooks/use-ahp-session-events'
import {useTaskAhpHistory} from '../../hooks/use-task-ahp-history'
import {useTaskHistoryRequired} from '../../contexts/new-task-context'
import {useAhpDebugEvents} from '../../hooks/use-ahp-debug-events'
import {EstablishedTaskChatInputActions, PersistedTaskChatInputActions} from './EstablishedTaskChatInputActions'
import {SessionResumeNotice} from './SessionResumeNotice'
import {McpLoadingNotice} from './McpLoadingNotice'
import {useSessionLoadingPresentation} from '../../hooks/use-session-loading-presentation'
import {
  AHP_DEBUG_LOG_TOOL_DEFINITION,
  createAhpDebugLogToolState,
  fulfilAhpDebugLogTool,
} from '../../ahp/ahp-debug-log-tool'
import {isAhpDebugLogEnabled} from '../../ahp/ahp-debug-log'
import {bindUserStartedTurn, markUserStartedTurn, unmarkUserStartedTurn} from '../../utils/artifacts/user-started-turns'
import resumeNoticeStyles from './SessionResumeNotice.module.css'
import styles from './EstablishedTaskChatInput.module.css'

interface EstablishedTaskChatInputProps extends TaskChatInputProps {
  initialSelectedSpace?: AhpSpaceSelection
  directTurnTelemetryContext?: DirectTurnTelemetryContext
  /** Launch path reported with session resume errors. */
  resumeErrorEntrypoint?: DirectTurnSurface
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  onModelChange?: (modelId: string, reasoningEffort: ReasoningEffort | undefined) => void
  onReasoningEffortChange?: (from: ReasoningEffort | undefined, to: ReasoningEffort | undefined) => void
  restoredPrompt?: string
  onRestoredPromptChange?: (prompt: string) => void
  onResumeIntent?: () => void
  onRestoredPromptAccepted?: (prompt: string) => void
  loadingPresentation?: 'local' | 'page'
  connectionPending?: boolean
  connectionError?: string
  connectionFailure?: RelayFailure
  newConversationHref?: string
  connectionProgress?: RelayConnectionProgress
  connectionPresentationPhase?: 'waking' | 'connecting'
  connectionSlow?: boolean
  canRetryConnection?: boolean
  connectionDraftOnly?: boolean
  readOnly?: boolean
  onResume?: () => void
  hidden?: boolean
  /** False keeps sends from claiming the active-client role, for sessions another client owns. */
  claimActiveClient?: boolean
}

function EstablishedTaskChatInputContent({
  directTurnTelemetryContext,
  resumeErrorEntrypoint,
  interactiveSandboxChat,
  claimActiveClient = true,
  activeSession,
  onClearPendingSession,
  payload,
  scrollToBottom,
  repoNwo,
  task,
  taskAgentId: _taskAgentId,
  onViewDiff,
  branchAliveChannel,
  textAreaRef,
  initialSelectedSpace,
  restoredPrompt,
  onRestoredPromptChange,
  onResumeIntent,
  onRestoredPromptAccepted,
  loadingPresentation = 'local',
  connectionPending,
  connectionError,
  connectionFailure,
  newConversationHref,
  connectionProgress,
  connectionPresentationPhase,
  connectionSlow,
  canRetryConnection,
  connectionDraftOnly = false,
  readOnly: preparationReadOnly = false,
  onResume,
  hidden = false,
}: EstablishedTaskChatInputProps) {
  const {pull, agentQuotaSnapshot: payloadAgentQuotaSnapshot, steeringEnabled, useTokenBasedBilling} = payload
  const viewMode = useViewMode()
  const {currentUserIsSessionOwner, currentUser} = useCurrentUser()
  const view = useOptionalEstablishedAhpSessionViewState()
  const agentQuotaSnapshot = useFreshQuotaSnapshot(payloadAgentQuotaSnapshot, activeSession, view.state)
  const isBlockedByQuota = agentQuotaSnapshot?.can_start_session === false
  const {notice: quotaNotice} = useEntitlementQuotaNotice({kind: 'agent', snapshot: agentQuotaSnapshot})
  const individualBudgetCtaShownRef = useRef(false)
  const trackIndividualBudgetCtaShown = useCallback((link: HTMLAnchorElement | null) => {
    if (!link || individualBudgetCtaShownRef.current) return
    individualBudgetCtaShownRef.current = true
    sendEvent('dotcom_chat.activate', {
      target: 'QUOTA_BANNER_EXCEEDED',
      action: 'shown',
      category: 'general_ipm_ubb_user_exhausted_alerts_individual',
      scope: 'personal',
      surface: 'agent_sessions_message_input',
    })
  }, [])
  const applyPermissionCommand = usePermissionCommandHandler()
  const resolveTaskHasRepository = useCallback(() => taskHasRepository(task), [task])
  const reportStaffCommandFeedback = useStaffDebugFeedbackToast()
  const establishedSession = useOptionalEstablishedAhpSession()
  const taskHistory = useTaskAhpHistory(activeSession?.task_id, undefined, !!activeSession && interactiveSandboxChat, {
    refetchOnMount: false,
  })
  const {chatState: sessionPersistedChatState} = useAhpSessionEvents({
    session: activeSession,
    enabled: !interactiveSandboxChat,
  })
  const taskPersistedChatState = taskHistory.data?.find(saved => saved.sessionId === activeSession?.id)?.bundle
    .defaultChatState
  const historyRequired = useTaskHistoryRequired(task.id)
  const persistedChatState = historyRequired
    ? interactiveSandboxChat
      ? taskPersistedChatState
      : sessionPersistedChatState
    : undefined
  const {mutateAsync: unarchiveTask} = useUnarchiveTask({taskId: task.id, repoNwo})
  const canAutoUnarchive = !!task.archived_at && pull?.state !== 'merged'
  const branchArtifact = getBranchArtifactData(task.artifacts)
  const baseRef = (!baseRefMatchesHeadRef(branchArtifact) && branchArtifact?.base_ref) || pull?.base_ref_name
  const headRef = branchArtifact?.head_ref || pull?.head_ref_name
  const hasContributedInstructionsProvider = useHasContributedInstructionsProvider()
  const contributedInstructionSnapshot = useOptionalContributedInstructionSnapshot()
  const legacyContributedInstructionSnapshot = useLegacyContributedInstructionSnapshot()
  const currentContributedInstructionSnapshot = hasContributedInstructionsProvider
    ? contributedInstructionSnapshot
    : legacyContributedInstructionSnapshot
  const internalTextAreaRef = useRef<HTMLTextAreaElement>(null)
  const composerTextAreaRef = textAreaRef ?? internalTextAreaRef
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const [actionPanelsHost, setActionPanelsHost] = useState<HTMLDivElement | null>(null)
  const getArtifactsFallbackFocus = () =>
    composerTextAreaRef.current ??
    inputContainerRef.current?.querySelector<HTMLElement>(
      ':is([data-testid="established-ahp-session-prompt"], [data-testid="established-ahp-session-confirmation"], [data-testid="chat-composer-ask-user"], [data-testid="chat-composer-confirmation"]) :is(input, textarea, button):not([disabled])',
    ) ??
    inputContainerRef.current?.querySelector<HTMLElement>('[data-testid="blocked-task-input"]') ??
    null
  const {applyShareCommand, feedback: shareCommandFeedback} = useShareCommandHandler({
    taskId: task.id,
    onDismissFeedback: () => getArtifactsFallbackFocus()?.focus(),
  })
  const {guardRepositoryCommand, feedback: repositoryCommandFeedback} = useRepositoryCommandGuard({
    resolveHasRepository: resolveTaskHasRepository,
    taskId: task.id,
    onDismissFeedback: () => getArtifactsFallbackFocus()?.focus(),
  })
  const taskDebugEvents = useAhpDebugEvents(task.id)
  const sessionDebugEvents = useAhpDebugEvents(activeSession?.id)
  const debugEvents = taskDebugEvents.length > 0 ? taskDebugEvents : sessionDebugEvents
  const debugEventsRef = useRef(debugEvents)
  const debugLogToolStateRef = useRef(createAhpDebugLogToolState())
  useLayoutEffect(() => {
    debugEventsRef.current = debugEvents
  }, [debugEvents])
  useLayoutEffect(() => {
    debugLogToolStateRef.current = createAhpDebugLogToolState()
  }, [task.id])
  const debugLogTool = useMemo<ClientTool>(
    () => ({
      definition: AHP_DEBUG_LOG_TOOL_DEFINITION,
      fulfil: call =>
        fulfilAhpDebugLogTool(
          JSON.parse(call.toolInput ?? '{}') as Record<string, unknown>,
          call,
          debugEventsRef.current,
          debugLogToolStateRef.current,
        ),
    }),
    [],
  )
  const {
    pendingPrompt,
    isPending: isSubmittingPrompt,
    handlePromptRespond,
  } = usePromptResponseSubmission({
    taskId: task.id,
    environmentId: activeSession?.environment_id,
    sessionId: activeSession?.id,
    enabled: !!activeSession && view.reachable,
    sessionState: activeSession?.state,
    isAhpSession: true,
    reachable: view.reachable,
  })
  const quotaBlockedText = (() => {
    const description = (quotaNotice?.description ?? "You don't have enough AI credits to start a session.").replace(
      'start a session',
      'start or steer a session',
    )
    const {primaryAction, secondaryAction} = quotaNotice ?? {}
    if (!primaryAction && !secondaryAction) return description
    const individualBudgetCta = primaryAction?.href === INCREASE_INDIVIDUAL_BUDGET_URL
    return (
      <>
        {description}{' '}
        {primaryAction && (
          <Link
            ref={individualBudgetCta ? trackIndividualBudgetCtaShown : undefined}
            href={primaryAction.href}
            inline
            onClick={
              individualBudgetCta
                ? () =>
                    sendEvent('dotcom_chat.activate', {
                      target: 'QUOTA_BANNER_EXCEEDED',
                      action: 'increase_budget',
                      category: 'general_ipm_ubb_user_exhausted_alerts_individual',
                      scope: 'personal',
                      surface: 'agent_sessions_message_input',
                    })
                : undefined
            }
          >
            {primaryAction.label}
          </Link>
        )}
        {secondaryAction && (
          <>
            {primaryAction ? ' or ' : ''}
            <Link href={secondaryAction.href} inline>
              {primaryAction ? 'upgrade your plan' : 'Upgrade your plan'}
            </Link>
          </>
        )}
        {'.'}
      </>
    )
  })()
  const blockedText =
    task.archived_at && !canAutoUnarchive
      ? 'This session has been archived.'
      : pull?.state === 'merged'
        ? 'This pull request was merged. Start a new session to continue iterating.'
        : pull?.state === 'closed'
          ? 'This pull request was closed. To continue iterating, reopen the pull request.'
          : !steeringEnabled
            ? 'Steering Copilot requires an active subscription.'
            : !currentUserIsSessionOwner && currentUser
              ? 'Only the task owner can steer this session.'
              : isBlockedByQuota
                ? quotaBlockedText
                : undefined
  const quotaBlockSelected = isBlockedByQuota && blockedText === quotaBlockedText
  const quotaBlockVisible = quotaBlockSelected && !preparationReadOnly && !hidden
  useEffect(() => {
    if (quotaBlockVisible && inputContainerRef.current) {
      announce(
        (quotaNotice?.description ?? "You don't have enough AI credits to start a session.").replace(
          'start a session',
          'start or steer a session',
        ),
        {assertive: true, element: inputContainerRef.current},
      )
    }
  }, [quotaBlockVisible, quotaNotice?.description])
  const readOnly = preparationReadOnly || view.passive || !!blockedText
  const draftOnly = Boolean(connectionDraftOnly || preparationReadOnly || blockedText || (onResume && !view.reachable))
  const resumeNoticeId = useId()
  const blockedMessageId = useId()
  const pageLoadingPresentation = useSessionLoadingPresentation()
  const showResumeNotice =
    draftOnly && Boolean(connectionError || connectionFailure || (loadingPresentation === 'local' && connectionPending))
  const connectionFocusReturn = useEstablishedAhpComposerFocusReturn(showResumeNotice ? 'connection' : undefined, () =>
    composerTextAreaRef.current?.focus(),
  )
  const handleResumeRetry = () => {
    onResume?.()
    requestAnimationFrame(() => composerTextAreaRef.current?.focus())
  }
  // Names the submission currently in flight, so its receipt binds to it and its
  // rollback cannot retire one belonging to a different pending submission.
  const armedSubmissionRef = useRef<number | undefined>(undefined)
  const handleSubmitSuccess = useCallback(
    (receipt?: DispatchReceipt) => {
      const armedSubmissionId = armedSubmissionRef.current
      armedSubmissionRef.current = undefined
      // The receipt decides this submission's fate. One that names a turn binds to
      // it. One that only names a queue entry, or no receipt at all because the
      // prompt configured the session instead of starting a turn, retires the
      // submission, so no later turn can inherit it.
      if (armedSubmissionId !== undefined) {
        if (receipt) bindUserStartedTurn(task.id, armedSubmissionId, receipt)
        else unmarkUserStartedTurn(task.id, armedSubmissionId)
      }
      onClearPendingSession()
      scrollToBottom()
    },
    [onClearPendingSession, scrollToBottom, task.id],
  )
  const handlePromptAccepted = useCallback(
    (prompt: string) => {
      if (restoredPrompt !== undefined) onRestoredPromptAccepted?.(prompt)
    },
    [onRestoredPromptAccepted, restoredPrompt],
  )
  const claimInteractiveClientTools = useCallback(
    (session: EstablishedAhpSession) =>
      session.commands.claimActiveClient([
        ...HOST_INDEPENDENT_CLIENT_TOOLS,
        ...(isAhpDebugLogEnabled() ? [debugLogTool] : []),
      ]),
    [debugLogTool],
  )
  const handleBeforeDispatch = useCallback(
    async (session: EstablishedAhpSession): Promise<void> => {
      if (interactiveSandboxChat && claimActiveClient) await claimInteractiveClientTools(session)
      armedSubmissionRef.current = markUserStartedTurn(task.id)
    },
    [claimActiveClient, claimInteractiveClientTools, interactiveSandboxChat, task.id],
  )
  // `onSubmitFailure` also fires for failures that happen before the dispatch runs,
  // such as preparing the prompt or configuring the session. Rolling back then would
  // take away the mark belonging to a different submission that is still waiting to
  // start, so only a submission that actually armed one gives it back.
  const handleSubmitFailure = useCallback(() => {
    const armedSubmissionId = armedSubmissionRef.current
    if (armedSubmissionId === undefined) return
    armedSubmissionRef.current = undefined
    unmarkUserStartedTurn(task.id, armedSubmissionId)
  }, [task.id])

  return (
    <div
      ref={inputContainerRef}
      data-testid="established-task-input-container"
      className={showResumeNotice ? resumeNoticeStyles.WithNotice : undefined}
      hidden={hidden}
    >
      {agentQuotaSnapshot && (!blockedText || (preparationReadOnly && quotaBlockSelected)) && (
        <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
      )}
      <div ref={setActionPanelsHost} className={styles.actionPanelsHost} />
      <div className={styles.combinedActionBars}>
        {viewMode !== 'sidePanel' &&
          (!isFeatureEnabled('copilot_mission_control_ahp_diff') && baseRef && headRef ? (
            <TaskChatInputHeader
              pull={pull}
              repoNwo={repoNwo}
              taskId={task.id}
              baseRef={baseRef}
              headRef={headRef}
              branchAliveChannel={branchAliveChannel}
              isSessionInProgress={!!activeSession}
              onViewDiff={onViewDiff}
            />
          ) : (
            <AhpTaskChatInputActions
              pull={pull}
              repoNwo={repoNwo}
              taskId={task.id}
              baseRef={baseRef ?? ''}
              headRef={headRef ?? ''}
              branchAliveChannel={branchAliveChannel}
              isSessionInProgress={!!activeSession}
              onViewDiff={onViewDiff}
            />
          ))}
        {activeSession ? (
          <SessionContextProvider session={activeSession}>
            {establishedSession ? (
              <EstablishedTaskChatInputActions
                taskId={task.id}
                sessionId={activeSession.id}
                persistedState={persistedChatState}
                liveState={view.state}
                focusTargetRef={composerTextAreaRef}
                getArtifactsFallbackFocus={getArtifactsFallbackFocus}
                onRefetchPersistedHistory={interactiveSandboxChat ? taskHistory.refetch : undefined}
                onViewDiff={onViewDiff}
                panelPortalTarget={actionPanelsHost}
              />
            ) : (
              <PersistedTaskChatInputActions
                taskId={task.id}
                sessionId={activeSession.id}
                persistedState={persistedChatState}
                focusTargetRef={composerTextAreaRef}
                getArtifactsFallbackFocus={getArtifactsFallbackFocus}
                onRefetchPersistedHistory={interactiveSandboxChat ? taskHistory.refetch : undefined}
                panelPortalTarget={actionPanelsHost}
              />
            )}
          </SessionContextProvider>
        ) : null}
      </div>
      {showResumeNotice && (
        <div {...connectionFocusReturn}>
          <SessionResumeNotice
            id={resumeNoticeId}
            errorTelemetry={{
              entrypoint: resumeErrorEntrypoint,
              taskId: activeSession?.task_id ?? task.id,
              sessionId: activeSession?.id,
              environmentId: activeSession?.environment_id,
            }}
            pending={Boolean(connectionPending)}
            error={connectionError}
            failure={connectionFailure}
            newConversationHref={newConversationHref}
            progress={connectionProgress}
            presentationPhase={connectionPresentationPhase}
            slow={connectionSlow}
            canRetry={canRetryConnection}
            onRetry={onResume ? handleResumeRetry : undefined}
            visible={!hidden}
          />
        </div>
      )}
      {shareCommandFeedback}
      {repositoryCommandFeedback}
      {blockedText && !preparationReadOnly && <BlockedTaskInput id={blockedMessageId}>{blockedText}</BlockedTaskInput>}
      {copilotFeatureFlags.mcpLoadingStatus &&
        !hidden &&
        !connectionPending &&
        !connectionError &&
        !connectionFailure &&
        establishedSession?.identity.sessionId === activeSession?.id &&
        establishedSession?.view.isCurrent() &&
        view.status === 'connected' &&
        view.reachable &&
        !view.error && <McpLoadingNotice key={activeSession?.id} customizations={view.sessionState?.customizations} />}
      <OverlayDirectionProvider preferredOverlaySide="outside-top">
        <AhpSessionComposer
          directTurnTelemetryContext={directTurnTelemetryContext}
          state={view.state}
          initialSelectedSpace={initialSelectedSpace}
          readOnly={readOnly}
          draftOnly={draftOnly}
          hidden={hidden}
          composerDescriptionId={
            [
              showResumeNotice
                ? resumeNoticeId
                : loadingPresentation === 'page'
                  ? pageLoadingPresentation?.descriptionId
                  : undefined,
              blockedText && !preparationReadOnly ? blockedMessageId : undefined,
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
          draftKey={task.id}
          takeoverEnabled
          suppressQueuedMessages
          pendingPrompt={pendingPrompt ?? undefined}
          onPromptRespond={handlePromptRespond}
          isSubmittingPrompt={isSubmittingPrompt}
          textAreaRef={composerTextAreaRef}
          beforeSubmit={
            canAutoUnarchive
              ? async () => {
                  await unarchiveTask()
                }
              : undefined
          }
          beforeDispatch={handleBeforeDispatch}
          onSubmitFailure={handleSubmitFailure}
          repository={{getCurrent: () => task.repository}}
          restrictionOwner={repoNwo?.split('/')[0]}
          tokenUsage={{persistedState: persistedChatState}}
          preserveDraftAgent
          preserveDraftModel
          autoTierEnabled={interactiveSandboxChat}
          reasoningEffortEnabled={copilotFeatureFlags.codingAgentReasoningEffort}
          confirmationTestId="established-ahp-session-confirmation"
          promptTestId="established-ahp-session-prompt"
          isStreaming={view.snapshot.state?.activeTurn !== undefined}
          initialValue={restoredPrompt}
          onValueChange={onRestoredPromptChange}
          onResumeIntent={onResumeIntent}
          onPromptAccepted={handlePromptAccepted}
          onSubmitSuccess={handleSubmitSuccess}
          failureMessage="Failed to submit latest message"
          isTokenBasedBillingEnabled={useTokenBasedBilling}
          contributedInstructions={toContributedInstructionsMetadata(
            currentContributedInstructionSnapshot,
            repoNwo ? 'repository' : 'chat',
          )}
          debugEvents={debugEvents}
          reportStaffCommandFeedback={reportStaffCommandFeedback}
          presentation="inline"
          sessionConfiguration={{
            modePicker: <EstablishedModeToolbarSlot taskId={task.id} disabled={draftOnly} />,
            applyPermissionCommand,
            applyShareCommand,
            guardRepositoryCommand,
          }}
        />
      </OverlayDirectionProvider>
    </div>
  )
}

export function EstablishedTaskChatInput(props: EstablishedTaskChatInputProps) {
  const {onModelChange, onReasoningEffortChange} = props
  const [selectedModelId, setSelectedModelId] = useLatestAgentModel()
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useLatestReasoningEffort()
  const supportedReasoningEfforts = useModelSupportedReasoningEfforts(selectedModelId)
  const {models} = useAgentModels()
  const [selectedSpace, setSelectedSpace] = useState<AhpSpaceSelection>()
  const selectModel = useSelectModelClearingReasoningEffort(
    setSelectedModelId,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
  )
  useReasoningEffortAutoFallback(selectedReasoningEffort, supportedReasoningEfforts, setSelectedReasoningEffort)
  const handleModelChange = useCallback(
    (modelId: string, _efforts?: readonly string[], options?: {reconcile?: boolean}) => {
      selectModel(modelId)
      if (!options?.reconcile && modelId !== selectedModelId) {
        const modelEfforts = models.find(candidate => candidate.id === modelId)?.capabilities?.supports
          ?.reasoning_effort
        onModelChange?.(modelId, reasoningEffortForModel(selectedReasoningEffort, modelEfforts))
      }
    },
    [models, onModelChange, selectModel, selectedModelId, selectedReasoningEffort],
  )
  const handleReasoningEffortChange = useCallback(
    (effort: ReasoningEffort | undefined, _efforts?: readonly string[], options?: {reconcile?: boolean}) => {
      setSelectedReasoningEffort(effort)
      if (!options?.reconcile && effort !== selectedReasoningEffort) {
        onReasoningEffortChange?.(selectedReasoningEffort, effort)
      }
    },
    [onReasoningEffortChange, selectedReasoningEffort, setSelectedReasoningEffort],
  )

  return (
    <EstablishedAhpComposerProvider
      model={{selectedModelId, onUpdateModel: handleModelChange}}
      reasoningEffort={{selectedReasoningEffort, onUpdateReasoningEffort: handleReasoningEffortChange}}
      space={{selectedSpace, onSelectSpace: setSelectedSpace}}
    >
      <EstablishedTaskChatInputContent {...props} />
    </EstablishedAhpComposerProvider>
  )
}

EstablishedTaskChatInput.displayName = 'EstablishedTaskChatInput'
